import React, { useState, useEffect } from "react";
import { fetchGuarantorLoans } from "../../sdk/guarantors/guarantors";
import { toast } from "react-toastify";

const Requestors = ({
  id,
  isRequestorsOpen,
  closeRequestorsOpen,
  guarantorId,
  selectedGuarantor,
}) => {
  const [loans, setLoans] = useState([]);
  let [totalPages, setTotalPages] = useState(null);
  const [displayedPage, setDisplayedPage] = useState(1);
  let [currentPage, setCurrentPage] = useState(1);

  const [limit, setLimit] = useState(10);
  const [totalRequestors, setTotalRequestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getGuarantorLoans = async () => {
    if (!guarantorId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchGuarantorLoans(
        guarantorId,
        currentPage,
        limit,
      );

      setLoans(response?.data);
      setDisplayedPage(Number(response?.meta?.page));
      setTotalPages(Math.ceil(Number(response?.meta?.totalPages)));
      setTotalRequestors(response?.meta?.total);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setError(error?.message || "Failed to fetch guarantor loans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGuarantorLoans();
  }, [currentPage, limit, guarantorId]);

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === i
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }
    return pages;
  };

  return (
    <>
      <div
        id={id}
        className={`${
          isRequestorsOpen ? "flex" : "hidden"
        } items-center justify-center fixed inset-0 z-50 bg-gray-800 bg-opacity-50`}
      >
        {/* Centered modal box */}
        <div className="relative flex bg-white shadow-lg rounded-lg w-[90%] sm:w-[75%] md:w-[65%] h-auto max-h-[90vh] md:h-[755px] overflow-hidden">
          <div className="flex flex-col w-full">
            {/* Header */}
            <div className="flex w-full items-center justify-end p-2">
              <button
                onClick={closeRequestorsOpen}
                type="button"
                className="bg-transparent rounded-lg text-sm ml-auto inline-flex items-center"
                data-modal-toggle={id}
              >
                <svg
                  width="61"
                  height="43"
                  viewBox="0 0 61 43"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_13891_3896)">
                    <path
                      d="M32.84 28L30.7 24.9L28.9 28H25.2L28.88 22.3L25.1 16.84H28.94L31.08 19.92L32.88 16.84H36.58L32.84 22.46L36.68 28H32.84Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_13891_3896">
                      <rect width="61" height="43" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 pb-0 max-h-[calc(90vh-60px)] md:max-h-[calc(755px-60px)]">
              <p className="text-[#000] text-xl font-bold mb-5">
                Loans Guaranteed by{" "}
                {selectedGuarantor
                  ?.split(" ")
                  ?.map(
                    (name) =>
                      name?.charAt(0)?.toUpperCase() +
                      name?.slice(1)?.toLowerCase(),
                  )
                  ?.join(" ")}{" "}
                ({guarantorId})
              </p>

              {loading && <p>Loading loans...</p>}
              {error && <p className="text-red-500">{error}</p>}

              {!loading && !error && loans?.length === 0 && (
                <p className="text-gray-500">
                  No loans found for this guarantor.
                </p>
              )}

              {!loading && !error && loans?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-[#E3F8FF] text-left sticky top-0 z-10">
                      <tr>
                        <th className="p-2 text-[14px] font-normal">
                          Loan Code
                        </th>
                        <th className="p-2 text-[14px] font-normal">
                          Borrower
                        </th>
                        <th className="p-2 text-[14px] font-normal">
                          Loan Amount
                        </th>
                        <th className="p-2 text-[14px] font-normal">
                          Amount
                          <br />
                          Guaranteed
                        </th>
                        <th className="p-2 text-[14px] font-normal">
                          Balance Held
                        </th>
                        <th className="p-2 text-[14px] font-normal">
                          Request Date
                        </th>
                        <th className="p-2 text-[14px] font-normal">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loans?.map((loan, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-1.5 text-[#037BD1] cursor-pointer">
                            {loan?.loanCode}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal">
                            {loan?.borrower
                              ?.split(" ")
                              ?.map(
                                (name) =>
                                  name?.charAt(0)?.toUpperCase() +
                                  name?.slice(1)?.toLowerCase(),
                              )
                              ?.join(" ")}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal">
                            KES &nbsp;
                            {Number(loan?.loanAmount || "0.00")?.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal">
                            KES &nbsp;
                            {Number(
                              loan?.amountGuaranteed || "0.00",
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal">
                            KES &nbsp;
                            {Number(
                              loan?.currentPrincipalHeld || "0.00",
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal">
                            {new Date(loan?.createdAt)?.toLocaleDateString()}
                          </td>
                          <td className="p-1.5 text-[14px] font-normal capitalize">
                            <td className="p-1.5 text-[14px] font-normal capitalize">
                              <div className="flex items-center gap-2">
                                {loan?.status?.toLowerCase() === "pending" && (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 15 15"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                                      fill="#554D4D"
                                    />
                                  </svg>
                                )}
                                {loan?.status?.toLowerCase() === "rejected" && (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <mask
                                      id="mask0_3697_3201"
                                      maskType="luminance"
                                      maskUnits="userSpaceOnUse"
                                      x="0"
                                      y="0"
                                      width="14"
                                      height="14"
                                    >
                                      <path
                                        d="M0 0.32002C0 0.154334 0.134315 0.0200195 0.3 0.0200195H13.7C13.8657 0.0200195 14 0.154334 14 0.32002V13.6801C14 13.8458 13.8657 13.9801 13.7 13.9801H0.3C0.134315 13.9801 0 13.8458 0 13.6801V0.32002Z"
                                        fill="white"
                                      />
                                    </mask>
                                    <g mask="url(#mask0_3697_3201)">
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M14 0.32002C14 0.154334 13.8657 0.0200195 13.7 0.0200195H0.3C0.134315 0.0200195 0 0.154334 0 0.32002V13.6801C0 13.8458 0.134315 13.9801 0.3 13.9801H13.7C13.8657 13.9801 14 13.8458 14 13.6801V0.32002ZM10.3647 3.88232C10.4159 3.82394 10.4549 3.75594 10.4793 3.68232C10.5038 3.6087 10.5133 3.53094 10.5072 3.45362C10.501 3.3763 10.4795 3.30097 10.4438 3.23208C10.408 3.16319 10.3588 3.10212 10.2991 3.05247C10.2393 3.00282 10.1702 2.96558 10.0958 2.94296C10.0214 2.92033 9.94322 2.91277 9.86585 2.92071C9.78848 2.92866 9.71348 2.95196 9.64527 2.98924C9.57706 3.02652 9.51701 3.07702 9.46867 3.13778L7.2302 5.81607C7.11028 5.95956 6.88973 5.95955 6.76981 5.81606L4.53162 3.13778C4.48299 3.07789 4.4229 3.02824 4.35485 2.9917C4.2868 2.95517 4.21215 2.93248 4.13523 2.92496C4.05832 2.91744 3.98067 2.92524 3.9068 2.94789C3.83293 2.97055 3.76432 3.00762 3.70494 3.05694C3.64556 3.10627 3.5966 3.16687 3.56091 3.23522C3.52522 3.30358 3.5035 3.37832 3.49702 3.45512C3.49055 3.53191 3.49944 3.60922 3.52318 3.68256C3.54693 3.75589 3.58505 3.8238 3.63533 3.88232L6.08003 6.8077C6.17309 6.91907 6.17309 7.08109 6.08003 7.19245L3.63533 10.1178C3.58412 10.1762 3.54513 10.2442 3.52066 10.3178C3.49619 10.3915 3.48673 10.4692 3.49284 10.5465C3.49895 10.6239 3.52051 10.6992 3.55625 10.7681C3.59198 10.837 3.64118 10.898 3.70093 10.9477C3.76068 10.9973 3.82979 11.0346 3.90419 11.0572C3.97859 11.0798 4.05678 11.0874 4.13415 11.0794C4.21152 11.0715 4.28652 11.0482 4.35473 11.0109C4.42294 10.9736 4.48299 10.9231 4.53133 10.8624L6.76981 8.18407C6.88973 8.04058 7.11027 8.04058 7.23019 8.18407L9.46867 10.8624C9.51701 10.9231 9.57706 10.9736 9.64527 11.0109C9.71348 11.0482 9.78848 11.0715 9.86585 11.0794C9.94322 11.0874 10.0214 11.0798 10.0958 11.0572C10.1702 11.0346 10.2393 10.9973 10.2991 10.9477C10.3588 10.898 10.408 10.837 10.4438 10.7681C10.4795 10.6992 10.501 10.6239 10.5072 10.5465C10.5133 10.4692 10.5038 10.3915 10.4793 10.3178C10.4549 10.2442 10.4159 10.1762 10.3647 10.1178L7.91997 7.19245C7.82691 7.08109 7.82691 6.91907 7.91997 6.8077L10.3647 3.88232Z"
                                        fill="#FF5757"
                                      />
                                    </g>
                                  </svg>
                                )}
                                {loan?.status?.toLowerCase() === "accepted" && (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8 0C8.73438 0 9.44271 0.09375 10.125 0.28125C10.8073 0.46875 11.4427 0.739583 12.0312 1.09375C12.6198 1.44792 13.1589 1.86458 13.6484 2.34375C14.138 2.82292 14.5573 3.36198 14.9062 3.96094C15.2552 4.5599 15.5234 5.19792 15.7109 5.875C15.8984 6.55208 15.9948 7.26042 16 8C16 8.73438 15.9062 9.44271 15.7188 10.125C15.5312 10.8073 15.2604 11.4427 14.9062 12.0312C14.5521 12.6198 14.1354 13.1589 13.6562 13.6484C13.1771 14.138 12.638 14.5573 12.0391 14.9062C11.4401 15.2552 10.8021 15.5234 10.125 15.7109C9.44792 15.8984 8.73958 15.9948 8 16C7.26562 16 6.55729 15.9062 5.875 15.7188C5.19271 15.5312 4.55729 15.2604 3.96875 14.9062C3.38021 14.5521 2.84115 14.1354 2.35156 13.6562C1.86198 13.1771 1.44271 12.638 1.09375 12.0391C0.744792 11.4401 0.476562 10.8021 0.289062 10.125C0.101562 9.44792 0.00520833 8.73958 0 8C0 7.26562 0.09375 6.55729 0.28125 5.875C0.46875 5.19271 0.739583 4.55729 1.09375 3.96875C1.44792 3.38021 1.86458 2.84115 2.34375 2.35156C2.82292 1.86198 3.36198 1.44271 3.96094 1.09375C4.5599 0.744792 5.19792 0.476562 5.875 0.289062C6.55208 0.101562 7.26042 0.00520833 8 0ZM12.7109 5.35156L11.6484 4.28906L6.5 9.4375L4.35156 7.28906L3.28906 8.35156L6.5 11.5625L12.7109 5.35156Z"
                                      fill="#0C722F"
                                    />
                                  </svg>
                                )}
                                <span>{loan?.status}</span>
                              </div>
                            </td>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer (pagination) */}
              <div className="h-[70px] bg-white w-full px-[20px] border-t border-gray-200 flex items-center justify-between mt-4">
                <div className="flex items-center gap-[20px]">
                  <p className="text-[#074073] !text-[14px]">Rows per page</p>
                  <select
                    name="page"
                    id="page"
                    className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                    value={limit}
                    onChange={handleLimitChange}
                  >
                    <option value="50">50</option>
                    <option value="30">30</option>
                    <option value="20">20</option>
                    <option value="10">10</option>
                  </select>
                  <p className="text-[#074073] text-[14px]">
                    {displayedPage} / {totalPages} ({totalRequestors} records)
                  </p>
                </div>

                <div className="flex items-center gap-[15px]">
                  <p
                    onClick={() =>
                      handlePageChange(Math.max(currentPage - 1, 1))
                    }
                    className="text-[14px] cursor-pointer text-[#074073]"
                  >
                    Prev
                  </p>
                  {renderPages()}
                  <div
                    className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                      Number(currentPage) > 3
                        ? "bg-[#074073] text-white"
                        : "bg-[#F5F5F5] text-[#074073]"
                    }`}
                  >
                    <p>{Number(currentPage) > 3 ? currentPage : "..."}</p>
                  </div>
                  <p
                    onClick={() =>
                      handlePageChange(Math.min(currentPage + 1, totalPages))
                    }
                    className="text-[14px] cursor-pointer text-[#074073]"
                  >
                    Next
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Requestors;
